import { CheckCircleFilled, RightOutlined, WarningOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Col, Empty, List, Row, Space, Statistic, Typography } from "antd";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type MessageKey } from "@/i18n";
import { usePoll } from "@/components/tabs/WorkTabs";

/**
 * Beranda (App Flow §2.3): order summary, low stock, sync status. A card the role
 * may not see is left out; one with nothing to show says so instead of a zero.
 */
export function Dashboard() {
  const { t } = useI18n();
  const { me, can } = useAuth();
  const navigate = useNavigate();

  const warehouses = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => unwrap(api.GET("/v1/warehouses")),
    enabled: can("settings.warehouses", "inventory.view", "warehouse.operate"),
  });
  const members = useQuery({
    queryKey: ["members"],
    queryFn: () => unwrap(api.GET("/v1/members")),
    enabled: can("settings.users"),
  });

  const summary = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => unwrap(api.GET("/v1/dashboard")),
    refetchInterval: usePoll(60_000),
  });
  const orders = summary.data?.orders;
  const stores = summary.data?.stores;
  const storeTotal = Object.values(stores ?? {}).reduce((a, b) => a + b, 0);
  const storeProblems = (stores?.reauth_required ?? 0) + (stores?.error ?? 0);

  const checklist: { key: MessageKey; done: boolean; to: string; show: boolean }[] = [
    { key: "dashboard.setup.marketplace", done: storeTotal > 0, to: "/app/channels", show: can("channels.manage") },
    {
      key: "dashboard.setup.warehouse",
      done: (warehouses.data?.items.length ?? 0) > 0,
      to: "/app/settings/warehouses",
      show: can("settings.warehouses"),
    },
    {
      key: "dashboard.setup.team",
      done: (members.data?.members.length ?? 0) + (members.data?.invitations.length ?? 0) > 1,
      to: "/app/settings/users",
      show: can("settings.users"),
    },
  ];
  const visible = checklist.filter((c) => c.show);

  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, fontSize: 28 }}>
        {t("dashboard.greeting", { name: me?.user.name ?? "" })}
      </Typography.Title>

      <Row gutter={[16, 16]}>
        {orders && (
          <SummaryCard title={t("dashboard.card.newOrders")} to="/app/orders?status=new" loading={summary.isPending}>
            {(orders.byStatus.new ?? 0) + (orders.byStatus.processing ?? 0) + orders.cancelRequests === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("dashboard.card.emptyOrders")} />
            ) : (
              <Space orientation="vertical" size={4}>
                <Statistic value={orders.byStatus.new ?? 0} suffix={t("status.new").toLowerCase()} />
                <Typography.Text type="secondary">{t("dashboard.card.processingValue", { n: orders.byStatus.processing ?? 0 })}</Typography.Text>
                {orders.cancelRequests > 0 && (
                  <Typography.Link onClick={() => navigate("/app/orders?status=all&cancel=1")}>
                    <WarningOutlined /> {t("dashboard.card.cancelRequestsValue", { n: orders.cancelRequests })}
                  </Typography.Link>
                )}
              </Space>
            )}
          </SummaryCard>
        )}
        {summary.data && summary.data.lowStock !== null && (
          <SummaryCard title={t("dashboard.card.lowStock")} to="/app/inventory?low=1" loading={summary.isPending}>
            {summary.data.lowStock === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("dashboard.card.emptyStock")} />
            ) : (
              <Statistic value={summary.data.lowStock} suffix="SKU" styles={{ content: { color: "var(--ant-color-warning)" } }} />
            )}
          </SummaryCard>
        )}
        {stores && (
          <SummaryCard title={t("dashboard.card.sync")} to="/app/channels" loading={summary.isPending}>
            {storeTotal === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("dashboard.card.emptySync")} />
            ) : (
              <Space orientation="vertical" size={4}>
                <Statistic value={stores.active ?? 0} suffix={`/ ${storeTotal}`} />
                <Typography.Text type="secondary">{t("dashboard.card.storesActive", { n: stores.active ?? 0 })}</Typography.Text>
                {storeProblems > 0 && (
                  <Typography.Text type="warning">
                    <WarningOutlined /> {t("dashboard.card.storesProblem", { n: storeProblems })}
                  </Typography.Text>
                )}
              </Space>
            )}
          </SummaryCard>
        )}

        {visible.length > 0 && (
          <Col xs={24}>
            <Card title={t("dashboard.setup.title")}>
              <List
                dataSource={visible}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(item.to)}
                    extra={<RightOutlined style={{ color: "var(--z-muted)" }} />}
                  >
                    <List.Item.Meta
                      avatar={
                        <CheckCircleFilled
                          style={{ fontSize: 20, color: item.done ? "var(--ant-color-success)" : "var(--z-line)" }}
                          aria-hidden
                        />
                      }
                      title={
                        <Typography.Text delete={item.done} type={item.done ? "secondary" : undefined}>
                          {t(item.key)}
                        </Typography.Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
}

function SummaryCard({ title, to, loading, children }: { title: string; to: string; loading: boolean; children: ReactNode }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <Col xs={24} lg={8}>
      <Card
        title={title}
        loading={loading}
        style={{ height: "100%" }}
        extra={
          <Button type="link" size="small" onClick={() => navigate(to)}>
            {t("dashboard.card.open")} <RightOutlined />
          </Button>
        }
      >
        {children}
      </Card>
    </Col>
  );
}
